import React, { useState } from "react";
import { twMerge } from "tailwind-merge";
import Tooltip from "../Tooltip/Tooltip";
import { assertNever } from "../../util";
import { Icon } from "../Icon/Icon";
export const colors = [
    "darkMain",
    "main",
    "link",
    "delete",
    "iconRed",
    "lightOrange",
    "surfaceOrange",
    "darkOrange",
    "gradientOrange",
    "purple",
    "darkPurple",
    "iconGreen",
    "iconDarkGreen",
    "textHeader",
    "iconInInput",
    "iconGrey",
    "grayText",
    "conicBlackOrange",
    "conicPurpleOrangePink",
    "surfaceMildYellow",
    "surfaceHighlight",
    "medicalDark",
    "medicalLight",
];
const getColorClass = (color, outlined) => {
    switch (color) {
        case "darkMain":
            return outlined
                ? "border-dark-main-color text-dark-main-color"
                : "bg-dark-main-color text-on-main-color";
        case "main":
            return outlined
                ? "border-main-color text-main-color"
                : "bg-main-color text-on-main-color";
        case "link":
            return outlined
                ? "border-link-color text-deep-main-color"
                : "bg-link-color text-on-main-color";
        case "delete":
            return outlined
                ? "border-delete-color text-delete-color"
                : "bg-delete-color text-on-main-color";
        case "iconRed":
            return outlined
                ? "border-icon-red-color text-icon-red-color"
                : "bg-icon-red-color text-on-main-color";
        case "lightOrange":
            return outlined
                ? "border-light-orange-color text-light-orange-color"
                : "bg-light-orange-color text-on-main-color";
        case "surfaceOrange":
            return outlined
                ? "border-surface-orange-color text-surface-orange-color"
                : "bg-surface-orange-color text-on-main-color";
        case "darkOrange":
            return outlined
                ? "border-dark-orange-color text-dark-orange-color"
                : "bg-dark-orange-color text-on-main-color";
        case "purple":
            return outlined
                ? "border-purple-color text-purple-color"
                : "bg-purple-color text-on-main-color";
        case "darkPurple":
            return outlined
                ? "border-dark-purple-color text-dark-purple-color"
                : "bg-dark-purple-color text-on-main-color";
        case "iconGreen":
            return outlined
                ? "border-icon-green-color text-icon-green-color"
                : "bg-icon-green-color text-on-main-color";
        case "iconDarkGreen":
            return outlined
                ? "border-icon-dark-green-color text-icon-dark-green-color"
                : "bg-icon-dark-green-color text-on-main-color";
        case "textHeader":
            return outlined
                ? "border-text-header-color text-text-header-color"
                : "bg-text-header-color text-on-main-color";
        case "iconInInput":
            return outlined
                ? "border-icon-in-input-color text-icon-in-input-color"
                : "bg-icon-in-input-color text-on-main-color";
        case "iconGrey":
            return outlined
                ? "border-icon-grey-color text-icon-grey-color"
                : "bg-icon-grey-color text-on-main-color";
        case "grayText":
            return outlined
                ? "border-gray-text-color text-gray-text-color"
                : "bg-gray-text-color text-on-main-color";
        case "gradientOrange":
            return outlined
                ? [
                    "border-none",
                    "text-light-orange-color",
                    "relative",
                    "before:content-['']",
                    "before:absolute",
                    "before:inset-0",
                    "before:rounded-[4px]",
                    "before:p-[1px]",
                    "before:bg-gradient-to-r before:from-[#f55050] before:to-[#f18c48]",
                    "before:[mask:linear-gradient(white,white)_content-box,linear-gradient(white,white)]",
                    "before:[mask-composite:xor]",
                ].join(" ")
                : "bg-[linear-gradient(4deg,#f55050_7%,#f18c48_60%)] text-on-main-color";
        case "conicBlackOrange":
            return outlined
                ? [
                    "border-none",
                    "text-header-color",
                    "relative",
                    "before:content-['']",
                    "before:absolute",
                    "before:inset-0",
                    "before:rounded-[4px]",
                    "before:p-[1px]",
                    "before:bg-gradient-to-r before:from-[#000] before:to-[#f18c48]",
                    "before:[mask:linear-gradient(white,white)_content-box,linear-gradient(white,white)]",
                    "before:[mask-composite:xor]",
                ].join(" ")
                : "bg-conic-black-orange-color text-on-main-color";
        case "conicPurpleOrangePink":
            return outlined
                ? [
                    "border-none",
                    "text-[#FF4E8E]",
                    "relative",
                    "before:content-['']",
                    "before:absolute",
                    "before:inset-0",
                    "before:rounded-[4px]",
                    "before:p-[1px]",
                    "before:bg-gradient-to-r before:from-[#9044B1] before:to-[#FF4E8E]",
                    "before:[mask:linear-gradient(white,white)_content-box,linear-gradient(white,white)]",
                    "before:[mask-composite:xor]",
                ].join(" ")
                : "bg-conic-purple-orange-pink-color text-on-main-color";
        case "surfaceMildYellow":
            return outlined
                ? "border-surface-mild-yellow-color text-surface-mild-yellow-color"
                : "bg-surface-mild-yellow-color border border-header-color text-header-color";
        case "surfaceHighlight":
            return outlined
                ? "border-surface-highlight text-surface-highlight"
                : "bg-surface-highlight text-header-color border border-header-color";
        case "medicalDark":
            return outlined
                ? "border-surface-medical-dark-color text-surface-medical-dark-color"
                : "bg-surface-medical-dark-color text-on-main-color";
        case "medicalLight":
            return outlined
                ? "border-surface-medical-light-color text-surface-medical-light-color"
                : "bg-surface-medical-light-color text-on-main-color";
        default:
            return assertNever(color);
    }
};
const ColorLabel = ({ id, text, iconClassName, className, onClick, tooltip, color = "darkMain", borderColor, outlined = false, hoverContent, }) => {
    const [isHovered, setIsHovered] = useState(false);
    const isLightOutlined = outlined && (color === "surfaceMildYellow" || color === "surfaceHighlight");
    const textEffectClasses = isLightOutlined
        ? "text-[#FFE580] [text-shadow:0_0_0.5px_#1E1E1E]"
        : undefined;
    const labelClasses = twMerge("typography-small__t flex h-[22px] max-h-[32px] min-h-[22px] w-fit max-w-[200px] items-center whitespace-nowrap rounded-[4px] py-[1px] px-1.5 transition-none", getColorClass(color, outlined), onClick && "cursor-pointer", isHovered && hoverContent ? "max-w-[220px] cursor-pointer" : null, (borderColor || outlined) && "border", outlined && "bg-transparent", borderColor, className);
    const iconClasses = twMerge("icon icon-sm", iconClassName, outlined ? "text-current" : "bg-on-main-color");
    const component = (React.createElement("div", { id: id, onClick: onClick, className: labelClasses, role: onClick ? "button" : undefined, onMouseEnter: () => hoverContent && setIsHovered(true), onMouseLeave: () => hoverContent && setIsHovered(false) },
        React.createElement("div", { className: "flex items-center gap-1" },
            iconClassName && React.createElement(Icon, { className: iconClasses }),
            text),
        hoverContent && (React.createElement("div", { className: twMerge("flex w-0 flex-nowrap items-center overflow-hidden opacity-0 transition-all duration-300", isHovered && "relative flex w-auto gap-[2px] pl-1.5 opacity-100") },
            React.createElement("div", { className: "mr-1 h-4 w-[1px] bg-current" }),
            hoverContent))));
    return tooltip ? (React.createElement(Tooltip, { content: tooltip.content, alignment: tooltip.alignment ?? "bottomRight", portalAnchor: tooltip.portalAnchor ?? "bottomLeft" }, component)) : (component);
};
export default ColorLabel;
//# sourceMappingURL=ColorLabel.js.map